import { useNavigate, useParams } from 'react-router-dom';
import { PermissionGuard } from '@/features/auth';
import { useProductQuery } from '../hooks/useProductQuery';
import { useProductMutations } from '../hooks/useProductMutations';
import { ProductForm } from '../components/ProductForm';
import type { ProductFormOutput } from '../schemas/product.schema';
import { Alert, TextLink } from '@/shared/components';

export function ProductEdit() {
    const { id = '' } = useParams();
    const navigate = useNavigate();
    const query = useProductQuery(id);
    const { updateMutation } = useProductMutations();

    if (!id) return <p className="text-mp-error">Invalid product ID.</p>;
    if (query.isLoading) return <p>Loading product...</p>;
    if (query.isError || !query.data) {
        return <Alert variant="error"><span>Product not found or failed to load.</span></Alert>;
    }

    const product = query.data;

    const handleSubmit = (data: ProductFormOutput) => {
        updateMutation.mutate({ id, payload: data }, {
            onSuccess: () => navigate(`/smr-admin/products/${id}`),
        });
    };

    return (
        <section className="space-y-4">
            <div className="flex items-center gap-2">
                <TextLink to={`/smr-admin/products/${id}`} color="secondary" className="text-sm">
                    &larr; Back to {product.name}
                </TextLink>
            </div>
            <h1 className="text-2xl font-semibold">Edit Product</h1>
            <PermissionGuard permission={{ resource: 'smr', action: 'product.edit' }}>
                <ProductForm
                    initialValues={product}
                    onSubmit={handleSubmit}
                    isLoading={updateMutation.isPending}
                    submitLabel="Save Changes"
                    onCancel={() => navigate(`/smr-admin/products/${id}`)}
                />
            </PermissionGuard>
        </section>
    );
}
